import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Logo } from './Logo';
import { emailVerificationService } from '../services/emailVerificationService';
import { checkEmail } from '../services/userService';
import { useNotifications } from '../hooks/useNotifications';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface EmailVerificationStepProps {
  onEmailVerified: (email: string) => void;
  onSwitchToLogin: () => void;
  onClose: () => void;
  /** When set, start in "code sent" state with this email (skip send step). */
  initialEmail?: string;
  /** Optional back button (e.g. to return to signup form). */
  onBack?: () => void;
}

interface FormErrors {
  email?: string;
  code?: string;
  general?: string;
}

const EmailVerificationStep: React.FC<EmailVerificationStepProps> = ({
  onEmailVerified,
  onSwitchToLogin,
  onClose,
  initialEmail = '',
  onBack,
}) => {
  const { t } = useTranslation();
  const { success, error, warning, info } = useNotifications();

  useBodyScrollLock(true);

  const [email, setEmail] = useState(initialEmail);
  const [verificationCode, setVerificationCode] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isServiceAvailable, setIsServiceAvailable] = useState(true);
  const [emailSent, setEmailSent] = useState(Boolean(initialEmail));

  React.useEffect(() => {
    if (initialEmail) {
      setEmail(initialEmail);
      setEmailSent(true);
    }
  }, [initialEmail]);

  React.useEffect(() => {
    checkServiceHealth();
  }, []);

  // Timer effect
  React.useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);

  const checkServiceHealth = async () => {
    const available = await emailVerificationService.checkServiceHealth();
    setIsServiceAvailable(available);
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (name === 'email') {
      setEmail(value);
      // Clear error when user starts typing
      if (errors.email) {
        setErrors(prev => ({
          ...prev,
          email: undefined
        }));
      }
    } else if (name === 'verificationCode') {
      // Only allow numbers and limit to 6 digits
      const numericValue = value.replace(/\D/g, '').slice(0, 6);
      setVerificationCode(numericValue);
      // Clear error when user starts typing
      if (errors.code) {
        setErrors(prev => ({
          ...prev,
          code: undefined
        }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!emailSent) {
      // Step 1: Send verification email
      if (!email.trim()) {
        setErrors({ email: 'Email is required' });
        return;
      }
      
      if (!validateEmail(email)) {
        setErrors({ email: 'Please enter a valid email address' });
        return;
      }

      try {
        setIsSending(true);
        setErrors({});
        
        // First check if email is already registered
        const emailCheckResult = await checkEmail(email);
        if (emailCheckResult.status === 1) {
          setErrors({ email: t('auth.signup.validation.emailAlreadyRegistered') });
          setIsSending(false);
          return;
        }
        
        info('Sending Verification', 'Sending verification email...');
        
        // Send verification email with only email
        await emailVerificationService.sendVerificationEmail({
          email: email
        });

        success('Verification Sent', 'Please check your email and enter the verification code');
        setEmailSent(true);
        setTimeLeft(30); // 30 seconds timer before resend is available
        
      } catch (err) {
        console.error('Email verification failed:', err);
        setErrors({ 
          general: err instanceof Error ? err.message : 'Failed to send verification email. Please try again.' 
        });
      } finally {
        setIsSending(false);
      }
    } else {
      // Step 2: Verify code
      if (!verificationCode.trim()) {
        setErrors({ code: 'Verification code is required' });
        return;
      }
      
      if (verificationCode.length !== 6) {
        setErrors({ code: 'Please enter a 6-digit verification code' });
        return;
      }

      try {
        setIsVerifying(true);
        setErrors({});
        
        info('Verifying Code', 'Verifying your code...');
        
        // Verify the code
        const result = await emailVerificationService.verifyEmailWithCode({
          email: email,
          code: verificationCode
        });

        if (result.success) {
          success('Email Verified', 'Your email has been verified successfully!');
          await Promise.resolve(onEmailVerified(email));
        }
      } catch (err) {
        console.error('Verification or signup failed:', err);
        setErrors({
          general: err instanceof Error ? err.message : 'Failed to verify code. Please try again.',
        });
      } finally {
        setIsVerifying(false);
      }
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      warning('Email Required', 'Please enter your email address first');
      return;
    }

    try {
      setIsResending(true);
      await emailVerificationService.resendVerificationEmail(email);
      success('Code Resent', 'A new verification code has been sent to your email');
      setTimeLeft(30); // Reset timer (30 seconds before resend is available)
    } catch (err) {
      error('Resend Failed', err instanceof Error ? err.message : 'Failed to resend verification code');
    } finally {
      setIsResending(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isServiceAvailable) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-dark-800 rounded-lg w-full max-w-md relative my-auto">
          <div className="p-6 text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-white mb-4">Service Unavailable</h1>
            <p className="text-gray-300 mb-6">
              Email verification service is currently unavailable. Please try again later.
            </p>
            <button
              onClick={onClose}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-dark-800 rounded-lg w-full max-w-md relative my-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-dark-700">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="flex items-center gap-1 text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm">{t('common.back', 'Back')}</span>
            </button>
          ) : (
            <div className="w-20" />
          )}
          <div className="text-center flex-1">
            <Logo />
            <div className="text-white text-xl mt-1">Email Verification</div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-300 transition-colors duration-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* General Error Display */}
          {errors.general && (
            <div className="bg-red-900/20 border border-red-500 rounded-lg p-4 mb-4">
              <p className="text-red-400 text-sm">{errors.general}</p>
            </div>
          )}

          {/* Info Section */}
          <div className="text-center mb-6">
            <div className="text-4xl mb-3">📧</div>
            <h2 className="text-lg font-semibold text-white mb-2">
              {emailSent ? 'Enter Verification Code' : 'Verify Your Email'}
            </h2>
            <p className="text-gray-300 text-sm">
              {emailSent 
                ? `We've sent a 6-digit code to ${email}. Please enter it below.`
                : 'We will send a verification code to your email address to get started.'
              }
            </p>
          </div>
          
          {/* Email Input */}
          <div>
            <label className="block text-white text-sm font-medium mb-2">Email Address</label>
            <input
              type="email"
              name="email"
              value={email}
              onChange={handleInputChange}
              placeholder="Enter your email address"
              disabled={emailSent}
              className={`w-full bg-dark-700 border rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none transition-colors duration-200 ${
                emailSent 
                  ? 'border-green-500 bg-green-900/20 cursor-not-allowed' 
                  : errors.email 
                    ? 'border-red-500 focus:border-red-400' 
                    : 'border-dark-600 focus:border-green-400'
              }`}
              required
            />
            {errors.email && (
              <p className="text-red-400 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          {/* Verification Code Input - Show after email is sent */}
          {emailSent && (
            <div>
              <label className="block text-white text-sm font-medium mb-2">Verification Code</label>
              <input
                type="text"
                name="verificationCode"
                value={verificationCode}
                onChange={handleInputChange}
                placeholder="000000"
                maxLength={6}
                className={`w-full bg-dark-700 border rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none transition-colors duration-200 text-center text-2xl tracking-widest ${
                  errors.code ? 'border-red-500 focus:border-red-400' : 'border-dark-600 focus:border-green-400'
                }`}
                required
              />
              {errors.code && (
                <p className="text-red-400 text-xs mt-1">{errors.code}</p>
              )}
              <p className="text-xs text-gray-300 mt-1">
                Enter the 6-digit code from your email
              </p>
            </div>
          )}

          {/* Timer */}
          {timeLeft > 0 && emailSent && (
            <div className="text-center">
              <p className="text-sm text-gray-300">
                Code expires in: <span className="text-yellow-400 font-mono">{formatTime(timeLeft)}</span>
              </p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={
              (!emailSent && (isSending || !email.trim())) ||
              (emailSent && (isVerifying || !verificationCode.trim() || verificationCode.length !== 6))
            }
            className={`w-full text-white font-medium py-3 rounded-lg transition-all duration-200 ${
              (!emailSent && (isSending || !email.trim())) ||
              (emailSent && (isVerifying || !verificationCode.trim() || verificationCode.length !== 6))
                ? 'bg-gray-500 cursor-not-allowed' 
                : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
            }`}
          >
            {isSending ? 'Sending...' : isVerifying ? 'Verifying...' : emailSent ? 'Verify Code' : 'Send Verification Email'}
          </button>

          {/* Resend Code - Show after email is sent */}
          {emailSent && (
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-300 mb-3">
                Didn't receive the code?
              </p>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={isResending || timeLeft > 0}
                className="text-blue-400 hover:text-blue-300 disabled:text-gray-500 disabled:cursor-not-allowed font-medium transition-colors"
              >
                {isResending ? 'Sending...' : 'Resend Code'}
              </button>
            </div>
          )}

          {/* Footer */}
          <div className="text-center">
            <span className="text-gray-300 text-sm">Already have an account? </span>
            <button
              type="button"
              onClick={onSwitchToLogin}
              className="text-yellow-400 hover:text-yellow-300 text-sm font-medium transition-colors duration-200"
            >
              Login here
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmailVerificationStep;
